import React from "react";
import PropTypes from "prop-types";
import { motion } from "framer-motion";
import { cn } from "../../utils/classNames";

const AnimatedRadio = React.forwardRef(
  ({ 
    className, 
    checked = false, 
    disabled = false,
    onChange,
    id,
    name,
    value,
    size = "md",
    ...props 
  }, ref) => {
    
    const sizeClasses = {
      sm: "h-4 w-4",
      md: "h-5 w-5", 
      lg: "h-6 w-6"
    };

    const dotSizes = {
      sm: "h-2 w-2",
      md: "h-2.5 w-2.5",
      lg: "h-3 w-3"
    };

    return (
      <div className="relative inline-flex">
        {/* Hidden native radio for accessibility */}
        <input
          type="radio"
          id={id}
          name={name}
          value={value}
          ref={ref}
          className="sr-only"
          checked={checked}
          disabled={disabled}
          onChange={onChange}
          {...props}
        />
        
        {/* Custom animated radio */}
        <motion.label
          htmlFor={id}
          className={cn(
            "relative cursor-pointer inline-flex items-center justify-center rounded-full",
            "border-2 transition-all duration-200 ease-out",
            sizeClasses[size],
            disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
            checked
              ? "border-primary bg-background shadow-button-3d"
              : "border-input/50 bg-background/60 backdrop-blur-xs hover:border-primary/50 hover:bg-primary/5",
            className
          )}
          whileHover={!disabled ? { scale: 1.05 } : {}}
          animate={{
            borderColor: checked ? "hsl(var(--primary))" : "hsl(var(--input) / 0.5)"
          }}
          transition={{ duration: 0.2, ease: "easeOut" }}
        >
          {/* Radio dot */}
          <motion.div
            className={cn(
              "rounded-full bg-primary",
              dotSizes[size]
            )}
            initial={false}
            animate={{
              scale: checked ? 1 : 0,
              opacity: checked ? 1 : 0
            }}
            transition={{
              type: "spring",
              stiffness: 500,
              damping: 30
            }}
          />
          
          {/* Ripple effect on select */}
          {checked && (
            <motion.div
              className="absolute inset-0 rounded-full border-2 border-primary"
              initial={{ scale: 1, opacity: 0.5 }}
              animate={{ scale: 1.8, opacity: 0 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
            />
          )}
        </motion.label>
      </div>
    );
  }
);

AnimatedRadio.displayName = "AnimatedRadio";

AnimatedRadio.propTypes = {
  className: PropTypes.string,
  checked: PropTypes.bool,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  size: PropTypes.oneOf(["sm", "md", "lg"]),
};

export default AnimatedRadio;